import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { fetchExecutionLogDetail, formatWorkflowDate } from '../../utils/workflowInsightsApi';
import { redactSecrets } from '../../utils/secretRedaction';

const WorkflowExecutionDetailsModal = ({
  open,
  onClose,
  token,
  workflowId,
  agencyId,
  logId,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !logId) return;
    let mounted = true;
    setLoading(true);
    fetchExecutionLogDetail({ token, workflowId, logId, agencyId })
      .then((data) => {
        if (mounted) setDetail(redactSecrets(data));
      })
      .catch(() => {
        if (mounted) setDetail(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [open, logId, token, workflowId, agencyId]);

  const copyJson = () => {
    navigator.clipboard?.writeText(JSON.stringify(detail, null, 2));
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="execution-details-modal">
        <DialogHeader>
          <DialogTitle>Execution details</DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-slate-500">Loading details...</p>}
        {!loading && detail && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-slate-500">Contact</span><p className="font-medium">{detail.contact_name}</p></div>
              <div><span className="text-slate-500">Action</span><p className="font-medium">{detail.action?.label}</p></div>
              <div><span className="text-slate-500">Status</span><p className="font-medium capitalize">{detail.status}</p></div>
              <div><span className="text-slate-500">Executed at</span><p className="font-medium">{formatWorkflowDate(detail.executed_at)}</p></div>
              <div><span className="text-slate-500">Workflow execution id</span><p className="font-mono text-xs">{detail.workflow_execution_id}</p></div>
              <div><span className="text-slate-500">Action id</span><p className="font-mono text-xs">{detail.action_id || '—'}</p></div>
            </div>
            {detail.error_message && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                {String(detail.error_message)}
              </div>
            )}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="font-medium text-slate-700">Payload</span>
                <Button variant="outline" size="sm" onClick={copyJson}>
                  <Copy size={14} className="mr-1" /> Copy JSON
                </Button>
              </div>
              <pre className="max-h-64 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
                {JSON.stringify({
                  input_payload: detail.input_payload,
                  output: detail.output,
                  provider_response: detail.provider_response,
                }, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowExecutionDetailsModal;
